import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback } from 'react';
import type { Tab, TabContent } from '../../../types/folder-label';
import { TabButton } from '../../atoms/folder-label/TabButton';
import { SectionHeader } from '../../molecules/folder-label/SectionHeader';
import { TimelineList } from '../../organisms/folder-label/TimelineList';

const useStyles = makeStyles(() => ({
    container: {
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: '32px',
    },
    mainCard: {
        maxWidth: '1024px',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    header: {
        padding: '24px',
        borderBottom: '1px solid #e5e7eb',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1f2937',
    },
    manageFoldersButton: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        textTransform: 'none',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: '#e5e7eb',
        },
    },
    content: {
        padding: '24px',
    },
}));

interface FolderLabelTemplateProps {
    title: string;
    tabs: Tab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    tabContent: TabContent;
    onManageFoldersClick: () => void;
    children?: React.ReactNode;
}

export const FolderLabelTemplate: React.FC<FolderLabelTemplateProps> = ({
    title,
    tabs,
    activeTab,
    onTabChange,
    tabContent,
    onManageFoldersClick,
    children,
}) => {
    const classes = useStyles();

    const handleTabClick = useCallback(
        (tabId: string) => () => onTabChange(tabId),
        [onTabChange]
    );

    return (
        <Box className={classes.container}>
            <Paper className={classes.mainCard} elevation={0}>
                <Box className={classes.header}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography className={classes.title}>{title}</Typography>
                        <Button
                            onClick={onManageFoldersClick}
                            className={classes.manageFoldersButton}
                            startIcon={<SettingsIcon />}
                        >
                            Manage Folders
                        </Button>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        {tabs.map((tab) => (
                            <TabButton
                                key={tab.id}
                                label={tab.label}
                                isActive={activeTab === tab.id}
                                onClick={handleTabClick(tab.id)}
                            />
                        ))}
                    </Stack>
                </Box>

                <Box className={classes.content}>
                    <SectionHeader title={tabContent.sectionTitle} count={tabContent.sectionCount} />
                    <TimelineList entries={tabContent.entries} />
                </Box>
            </Paper>

            {children}
        </Box>
    );
};
